import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
  alpha,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import {
  getUrinalysisById,
  updateUrinalysis,
} from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";


const EditUrinalysisModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    infection_type: "",
    action_taken: "",
    further_action_taken: "",
    date_time: "",
    handover: false,
    manager: false,
  });

  const infectionTypes = [
    { value: "No abnormal colour", label: "No abnormal colour", color: "#10B981" },
    { value: "Dark", label: "Dark", color: "#F59E0B" },
    { value: "Brown green/strong yellow", label: "Brown green/strong yellow", color: "#D97706" },
    { value: "Bright red/red-brown", label: "Bright red/red-brown", color: "#EF4444" },
    { value: "Clear", label: "Clear", color: "#3B82F6" },
  ];

  const { data: urinalysis, isLoading, refetch } = useQuery(
    [QK.urinalysis, selectedID],
    () => getUrinalysisById(selectedID),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            infection_type: data.infection_type || "",
            action_taken: data.action_taken || "",
            further_action_taken: data.further_action_taken || "",
            date_time: data.date_time || "",
            handover: data.handover || false,
            manager: data.manager || false,
          });
        }
      },
    }
  );

  useEffect(() => {
    if (show && selectedID) {
      refetch();
    }
  }, [show, selectedID, refetch]);

  const updateMutation = useMutation(
    (newData) => updateUrinalysis(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Urinalysis updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.urinalysis, clientId]);
        queryClient.invalidateQueries([QK.urinalysis, selectedID]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to update urinalysis",
          {
            variant: "error",
          }
        );
      },
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedData = {
      ...formData,
      client: clientId,
    };
    updateMutation.mutate(updatedData);
  };

  const handleDialogClose = () => {
    setFormData({
      infection_type: "",
      action_taken: "",
      further_action_taken: "",
      date_time: "",
      handover: false,
      manager: false,
    });
    handleClose();
  };

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Urinalysis Entry
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update urinalysis record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <CustomSelect
                label="Infection Type (Result Color) *"
                name="infection_type"
                selectedValue={formData.infection_type}
                onChange={handleChange}
                options={infectionTypes}
                placeholder="Select"
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomDateTime
                label="Date & Time *"
                name="date_time"
                value={formData.date_time}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextArea
                label="Action Taken"
                name="action_taken"
                value={formData.action_taken}
                onChange={handleChange}
                rows={2}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                  FURTHER TESTING
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <CustomTextArea
                label="Further Action Taken"
                name="further_action_taken"
                value={formData.further_action_taken}
                onChange={handleChange}
                rows={2}
                placeholder="Details of urine sent for testing..."
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="manager"
                      checked={formData.manager}
                      onChange={handleChange}
                      sx={{
                        color: "#D0D5DD",
                        "&.Mui-checked": {
                          color: "#2469F0",
                        },
                      }}
                    />
                  }
                  label="Share with Manager"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="handover"
                      checked={formData.handover}
                      onChange={handleChange}
                      sx={{
                        color: "#D0D5DD",
                        "&.Mui-checked": {
                          color: "#2469F0",
                        },
                      }}
                    />
                  }
                  label="Handover"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={updateMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isLoading}
            startIcon={
              updateMutation.isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 3,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {updateMutation.isLoading ? "Saving..." : "Update Urinalysis"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUrinalysisModal;